import {
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { Hotkey } from "components/identifyEventsTool/guide/Hotkey";
import { PLAYBACK_SPEEDS } from "components/scrapEyeDataDashboard/VideoPlayer/constants";
import { useVideoPlayerGroupContextOptional } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoPlayerGroupContext";
import { HotkeyId } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { useAltToggleMode } from "components/scrapEyeLabelling/hooks/useAltToggleMode";
import { useTenantTranslation } from "hooks/formatters";
import { getModifierKey } from "hooks/usePlatform";
import { EditableHotkey } from "../guide/EditableHotkey";
import { ReactNode, useState } from "react";
import { AreaHotkeyConfigModal } from "components/scrapEyeLabelling/modals/AreaHotkeyConfigModal";
import { MaterialHotkeyConfigModal } from "components/scrapEyeLabelling/modals/MaterialHotkeyConfigModal";
import { AreaColorMap } from "components/scrapEyeLayout/scrapEyeLayoutCommon";
import { MaterialRead } from "src/store/api/generatedApi";

const StaticHotkeyGroup = ({
  keys,
  description,
}: {
  keys: string[];
  description: string;
}) => {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Stack direction="row" spacing={0.5}>
        {keys.map((key) => (
          <Hotkey key={key} label={key} />
        ))}
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Stack>
  );
};

const HotkeyGroup = ({
  hotkeyId,
  description,
}: {
  hotkeyId: HotkeyId;
  description: string;
}) => {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <EditableHotkey hotkeyId={hotkeyId} />
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Stack>
  );
};

const MultiKeyHotkeyGroup = ({
  hotkeyIds,
  description,
}: {
  hotkeyIds: readonly HotkeyId[];
  description: string;
}) => {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Stack direction="row" spacing={0.5}>
        {hotkeyIds.map((hotkeyId) => (
          <EditableHotkey key={hotkeyId} hotkeyId={hotkeyId} />
        ))}
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Stack>
  );
};

export type HotkeyDescription = Readonly<{
  hotkeyId: HotkeyId;
  description: string;
}>;

const NO_EXTRA_HOTKEYS: readonly HotkeyDescription[] = [];

type HotkeyExplanationProps = Readonly<{
  eventHotkeys: readonly HotkeyDescription[];
  areaColors?: AreaColorMap;
  materials?: readonly MaterialRead[];
  location?: string;
  labelRecordingPlaybackRate?: number;
  onLabelRecordingPlaybackRateChange?: (rate: number) => void;
  extraModifierControls?: ReactNode;
  navigationWindowMinutes?: number;
  extraHotkeys?: readonly HotkeyDescription[];
  hotkeyConfigDisableScopes?: readonly string[];
}>;

export const HotkeyExplanation = ({
  eventHotkeys,
  areaColors,
  materials,
  location,
  labelRecordingPlaybackRate,
  onLabelRecordingPlaybackRateChange,
  extraModifierControls,
  navigationWindowMinutes,
  extraHotkeys = NO_EXTRA_HOTKEYS,
  hotkeyConfigDisableScopes,
}: HotkeyExplanationProps) => {
  const { t } = useTenantTranslation();
  const { alt, shift, ctrl } = getModifierKey();
  const videoPlayerGroupContext = useVideoPlayerGroupContextOptional();
  const hasAngleSwitching =
    (videoPlayerGroupContext?.videoPlayerGroups.length ?? 0) > 0 &&
    videoPlayerGroupContext?.videoPlayerGroups.some((g) => g.angles.length > 1);
  const { altToggleMode, setAltToggleMode } = useAltToggleMode();

  const [isAreaHotkeyConfigOpen, setIsAreaHotkeyConfigOpen] = useState(false);
  const [isMaterialHotkeyConfigOpen, setIsMaterialHotkeyConfigOpen] =
    useState(false);

  const showLabellingSpeed =
    labelRecordingPlaybackRate != null &&
    onLabelRecordingPlaybackRateChange != null;

  return (
    <Stack spacing={1} alignItems="center" width="100%">
      <Typography variant="caption" color="text.secondary" fontStyle="italic">
        {t("clickAnyKeyToCustomize")}
      </Typography>
      <Stack spacing={2} direction="row" justifyContent="center" width="100%">
        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {t("playback")}
          </Typography>
          <Stack spacing={1}>
            <HotkeyGroup hotkeyId="playPause" description={t("playPause")} />
            <MultiKeyHotkeyGroup
              hotkeyIds={["rewindLeft", "forwardRight"]}
              description={t("rewindForward")}
            />
            <MultiKeyHotkeyGroup
              hotkeyIds={["speedUp", "speedDown"]}
              description={t("speed")}
            />
            {hasAngleSwitching ? (
              <>
                <HotkeyGroup
                  hotkeyId="cycleAngles"
                  description={t("cycleAngles")}
                />
                <HotkeyGroup
                  hotkeyId="resetAnglesToAuto"
                  description={t("resetAnglesToAuto")}
                />
              </>
            ) : null}
          </Stack>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {t("timeline")}
          </Typography>
          <Stack spacing={1}>
            <StaticHotkeyGroup
              keys={[ctrl, t("scroll")]}
              description={t("zoomTimeline")}
            />
            <StaticHotkeyGroup
              keys={[ctrl, "0"]}
              description={t("resetZoom")}
            />
          </Stack>
        </Stack>

        {navigationWindowMinutes != null || extraHotkeys.length > 0 ? (
          <Stack spacing={1}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              {t("navigation")}
            </Typography>
            <Stack spacing={1}>
              {navigationWindowMinutes != null ? (
                <>
                  <MultiKeyHotkeyGroup
                    hotkeyIds={["nextItem", "previousItem"]}
                    description={t("nextPreviousUnverifiedPeriod")}
                  />
                  <MultiKeyHotkeyGroup
                    hotkeyIds={["jumpBack", "jumpForward"]}
                    description={t("backForwardMinutes", {
                      minutes: navigationWindowMinutes,
                    })}
                  />
                </>
              ) : null}
              {extraHotkeys.map((hotkey) => (
                <HotkeyGroup
                  key={hotkey.hotkeyId}
                  hotkeyId={hotkey.hotkeyId}
                  description={hotkey.description}
                />
              ))}
            </Stack>
          </Stack>
        ) : null}

        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {t("events")}
          </Typography>
          <Stack spacing={1}>
            <HotkeyGroup hotkeyId="liftEvent" description={t("lift")} />
            <HotkeyGroup hotkeyId="dropEvent" description={t("drop")} />
            <HotkeyGroup hotkeyId="addFlag" description={t("addFlag")} />
            <HotkeyGroup
              hotkeyId="reverseLiftDrop"
              description={t("reverseLiftDrop")}
            />
            <HotkeyGroup
              hotkeyId="editLastEvent"
              description={t("editEventArea")}
            />
            <HotkeyGroup
              hotkeyId="areaMaterialMapping"
              description={t("areaMaterialMapping")}
            />
            <HotkeyGroup
              hotkeyId="deleteEvent"
              description={t("deleteSelected")}
            />
            {eventHotkeys.map((hotkey) => (
              <HotkeyGroup
                key={hotkey.hotkeyId}
                hotkeyId={hotkey.hotkeyId}
                description={hotkey.description}
              />
            ))}
            <StaticHotkeyGroup keys={[ctrl, "Z"]} description={t("undo")} />
            <StaticHotkeyGroup
              keys={[ctrl, shift, "Z"]}
              description={t("redo")}
            />
          </Stack>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {t("modifiers")}
          </Typography>
          <Stack spacing={1}>
            <StaticHotkeyGroup
              keys={[alt, t("key")]}
              description={t("quickAreaEvent")}
            />
            <StaticHotkeyGroup
              keys={[shift, t("key")]}
              description={t("assignMaterial")}
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={altToggleMode}
                  onChange={(e) => {
                    setAltToggleMode(e.target.checked);
                  }}
                  // focus makes hotkeys not work
                  onFocus={(e) => {
                    e.target.blur();
                  }}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  {t("altToggleMode")}
                </Typography>
              }
              onMouseDown={(e) => {
                e.preventDefault();
              }}
              sx={{ ml: 0, mt: 0.5 }}
            />
            {extraModifierControls}
          </Stack>
        </Stack>

        {showLabellingSpeed || areaColors != null || materials != null ? (
          <Stack spacing={1}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
            >
              {t("configure")}
            </Typography>
            <Stack spacing={1}>
              {labelRecordingPlaybackRate != null &&
              onLabelRecordingPlaybackRateChange != null ? (
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>{t("labellingSpeed")}</InputLabel>
                  <Select
                    value={labelRecordingPlaybackRate}
                    onChange={(e) => {
                      onLabelRecordingPlaybackRateChange(e.target.value);
                    }}
                    label={t("labellingSpeed")}
                  >
                    {PLAYBACK_SPEEDS.map((speed) => (
                      <MenuItem key={speed} value={speed}>
                        {speed}
                        {`x`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
              {areaColors != null ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SettingsIcon />}
                  onClick={() => {
                    setIsAreaHotkeyConfigOpen(true);
                  }}
                  sx={{ textTransform: "none", justifyContent: "flex-start" }}
                >
                  {t("areaHotkeys")}
                </Button>
              ) : null}
              {materials != null ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SettingsIcon />}
                  onClick={() => {
                    setIsMaterialHotkeyConfigOpen(true);
                  }}
                  sx={{ textTransform: "none", justifyContent: "flex-start" }}
                >
                  {t("materialHotkeys")}
                </Button>
              ) : null}
            </Stack>
          </Stack>
        ) : null}
      </Stack>

      {/* Hotkey configuration modals */}
      {areaColors != null ? (
        <AreaHotkeyConfigModal
          open={isAreaHotkeyConfigOpen}
          onClose={() => {
            setIsAreaHotkeyConfigOpen(false);
          }}
          disableScopes={hotkeyConfigDisableScopes}
          areaColors={areaColors}
          location={location}
        />
      ) : null}
      {materials != null ? (
        <MaterialHotkeyConfigModal
          open={isMaterialHotkeyConfigOpen}
          onClose={() => {
            setIsMaterialHotkeyConfigOpen(false);
          }}
          materials={materials}
          disableScopes={hotkeyConfigDisableScopes}
        />
      ) : null}
    </Stack>
  );
};
